import { Component, OnInit, Input } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'youtube-player',
  standalone: true,
  styleUrls: ['./youtube-player.scss'],
  templateUrl: './youtube-player.html'
})
export class YoutubePlayerComponent implements OnInit {
  @Input() vid!: string;

  id: string = '';
  dangerousVideoUrl!: string;
  videoUrl!: SafeResourceUrl;

  constructor(private sanitizer: DomSanitizer) {}

  ngOnInit() {
    if (!this.isYouTubeUrl(this.vid)) {
      return;
    }
    this.id = this.extractYouTubeId(this.vid);

    if (this.id) {
      // transform into embed url
      this.updateVideoUrl('https://www.youtube.com/embed/' + this.id + '?controls=0&rel=0&modestbranding=1&playsinline=1&showinfo=0');
    }
  }

  private isYouTubeUrl(input: string): boolean {
    try {
      const { hostname } = new URL(input);
      const host = hostname.replace(/^www\./, '').toLowerCase();
      return (
        host.endsWith('youtube.com') ||
        host === 'youtu.be' ||
        host.endsWith('youtube-nocookie.com')
      );
    } catch {
      // Not a URL: allow raw ID scenario
      return input.length === 11 && /^[\w-]{11}$/.test(input);
    }
  }

  private extractYouTubeId(input: string): string {
    // Accept raw 11-char IDs directly
    if (input && input.length === 11 && /^[\w-]{11}$/.test(input)) {
      return input;
    }

    try {
      const url = new URL(input);
      const host = url.hostname.replace(/^www\./, '').toLowerCase();

      // youtube.com / m.youtube.com / youtube-nocookie.com
      if (host.endsWith('youtube.com') || host.endsWith('youtube-nocookie.com')) {
        // watch?v=VIDEOID
        const v = url.searchParams.get('v');
        if (v && v.length === 11) return v;

        const parts = url.pathname.split('/').filter(Boolean);

        // shorts/VIDEOID
        const shortsIndex = parts.indexOf('shorts');
        if (shortsIndex !== -1 && parts[shortsIndex + 1] && parts[shortsIndex + 1].length === 11) {
          return parts[shortsIndex + 1];
        }

        // embed/VIDEOID
        const embedIndex = parts.indexOf('embed');
        if (embedIndex !== -1 && parts[embedIndex + 1] && parts[embedIndex + 1].length === 11) {
          return parts[embedIndex + 1];
        }
      }

      // youtu.be/VIDEOID
      if (host === 'youtu.be') {
        const id = url.pathname.split('/').filter(Boolean)[0];
        if (id && id.length === 11) return id;
      }
    } catch {
      // Not a URL; fall through
    }

    return '';
  }

  updateVideoUrl(vid: string) {
    // Appending an ID to a YouTube URL is safe.
    // Always make sure to construct SafeValue objects as
    // close as possible to the input data so
    // ?controls=0&rel=0&modestbranding=1&playsinline=1&showinfo=0
    // that it's easier to check if the value is safe.
    this.dangerousVideoUrl = vid;
    this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.dangerousVideoUrl);
  }

  // Linkify youtube URLs which are not already links.
  linkifyYouTubeURLs(text: string) {
    var re = /https?:\/\/(?:[0-9A-Z-]+\.)?(?:youtu\.be\/|youtube(?:-nocookie)?\.com\S*?[^\w\s-])([\w-]{11})(?=[^\w-]|$)(?![?=&+%\w.-]*(?:['"][^<>]*>|<\/a>))[?=&+%\w.-]*/ig;
    return text.replace(re,'$1');
  }
}
